import { z } from 'zod';
import { DOCUMENT_DISCOUNT_PERCENTAGE_BOUND_ISSUE, DocumentDiscountTypeSchema, isDocumentDiscountWithinBounds, } from './document-discount.js';
import { AdHocInvoiceLineItemSchema, DocumentTaxTypeSchema, RegistryLinkedInvoiceLineItemSchema, } from './invoices.js';
import { LineItemDiscountTypeSchema, LineItemUnitTypeSchema } from './line-items.js';
import { emptyToUndefined, optionalStr } from './utils.js';
/** The possible lifecycle statuses for a quote. */
export const QuoteStatusSchema = z.enum([
    'DRAFT',
    'SENT',
    'ACCEPTED',
    'DECLINED',
    'EXPIRED',
    'SUPERSEDED',
    'INVOICED',
]);
const QuoteFieldsSchema = z.object({
    clientId: z.string().min(1, { error: 'You must select a client from the list' }),
    notes: optionalStr,
    terms: optionalStr,
    taxType: DocumentTaxTypeSchema.default('NONE'),
    taxRate: z.coerce
        .number()
        .min(0, { error: 'Tax rate cannot be negative' })
        .max(1, { error: 'Tax rate cannot exceed 100%' })
        .default(0),
    discount: z.coerce.number().min(0, { error: 'Discount cannot be negative' }).default(0),
    discountType: DocumentDiscountTypeSchema.default('NONE'),
    expiresAt: z
        .string()
        .optional()
        .transform(emptyToUndefined)
        .pipe(z.iso.datetime().optional()),
    lineItems: z
        .array(z.union([RegistryLinkedInvoiceLineItemSchema, AdHocInvoiceLineItemSchema]))
        .optional(),
});
/** Request body for creating a new quote. */
export const CreateQuoteSchema = QuoteFieldsSchema.refine(isDocumentDiscountWithinBounds, DOCUMENT_DISCOUNT_PERCENTAGE_BOUND_ISSUE);
/** Partial version of {@link CreateQuoteSchema} for PATCH updates — all fields optional. */
export const UpdateQuoteSchema = QuoteFieldsSchema.partial().refine(isDocumentDiscountWithinBounds, DOCUMENT_DISCOUNT_PERCENTAGE_BOUND_ISSUE);
const QuoteLineItemFieldsSchema = z.object({
    lineItemId: optionalStr,
    title: z.string().min(1, { error: 'Title must contain at least 1 character' }).max(255),
    description: optionalStr,
    unitPrice: z.coerce
        .number({ error: 'Unit price must be a number' })
        .min(0, { error: 'Unit price must be greater than or equal to 0' }),
    unitType: LineItemUnitTypeSchema.default('HOUR'),
    quantity: z.coerce.number().positive(),
    discount: z.coerce.number().min(0).default(0),
    discountType: LineItemDiscountTypeSchema.default('NONE'),
    taxable: z.boolean().default(true),
});
/** Request body for creating a quote line item. */
export const CreateQuoteLineItemSchema = QuoteLineItemFieldsSchema.refine(isDocumentDiscountWithinBounds, DOCUMENT_DISCOUNT_PERCENTAGE_BOUND_ISSUE);
/** Partial version of {@link CreateQuoteLineItemSchema} for PATCH updates. */
export const UpdateQuoteLineItemSchema = QuoteLineItemFieldsSchema.partial().refine(isDocumentDiscountWithinBounds, DOCUMENT_DISCOUNT_PERCENTAGE_BOUND_ISSUE);
/** Single item in a batch update request — `id` present means update, absent means create. */
export const BatchQuoteLineItemSchema = z
    .object({
    id: z.string().optional(),
    lineItemId: z.string().optional(),
    title: z
        .string()
        .min(1, { error: 'Title must contain at least 1 character' })
        .max(255),
    description: z.string().optional(),
    unitPrice: z.coerce
        .number({ error: 'Unit price must be a number' })
        .min(0, { error: 'Unit price must be greater than or equal to 0' }),
    unitType: LineItemUnitTypeSchema.default('HOUR'),
    quantity: z.coerce.number().positive(),
    discount: z.coerce.number().min(0).default(0),
    discountType: LineItemDiscountTypeSchema.default('NONE'),
    taxable: z.boolean().default(true),
})
    .refine(isDocumentDiscountWithinBounds, DOCUMENT_DISCOUNT_PERCENTAGE_BOUND_ISSUE);
/** Request body for batch-updating all line items on a quote. */
export const BatchUpdateQuoteLineItemsSchema = z.object({
    lineItems: z.array(BatchQuoteLineItemSchema),
});
/** Request body for converting an accepted quote to an invoice. */
export const ConvertQuoteSchema = z.object({
    invoiceDate: z.iso.datetime(),
    dueDate: z.iso.datetime(),
    remindersEnabled: z.boolean().optional(),
    /**
     * The picked reminder schedule; null or omitted uses the client's own, else the
     * default.
     */
    reminderScheduleId: z.string().min(1).nullable().optional(),
});
/** A single milestone invoice's allocation within a split request. */
export const ConvertQuoteSplitItemSchema = z.object({
    label: z.string().min(1).max(120),
    issueDate: z.iso.datetime(),
    dueDate: z.iso.datetime(),
    percentage: z.number().int().min(1).max(99).optional(),
    lineItemAllocations: z
        .array(z.object({ quoteLineItemId: z.string(), quantity: z.coerce.number().positive() }))
        .min(1)
        .optional(),
});
/** Request body for splitting an accepted quote into 2-5 milestone invoices. */
export const ConvertQuoteSplitSchema = z
    .object({
    mode: z.enum(['PERCENTAGE', 'LINE_ITEM']),
    splits: z.array(ConvertQuoteSplitItemSchema).min(2).max(5),
    remindersEnabled: z.boolean().optional(),
    /**
     * The picked reminder schedule; null or omitted uses the client's own, else the
     * default.
     */
    reminderScheduleId: z.string().min(1).nullable().optional(),
})
    .refine((data) => data.mode !== 'PERCENTAGE' ||
    data.splits.every((split) => split.percentage !== undefined), {
    message: 'percentage is required for every split in PERCENTAGE mode',
    path: ['splits'],
})
    .refine((data) => data.mode !== 'LINE_ITEM' ||
    data.splits.every((split) => split.lineItemAllocations !== undefined), {
    message: 'lineItemAllocations is required for every split in LINE_ITEM mode',
    path: ['splits'],
});
/** Request body for transitioning a quote's status. */
export const UpdateQuoteStatusSchema = z
    .object({
    status: QuoteStatusSchema,
    declineReason: optionalStr,
})
    .refine((data) => data.status !== 'DECLINED' || data.declineReason !== undefined, {
    message: 'declineReason is required when status is DECLINED',
    path: ['declineReason'],
});
//# sourceMappingURL=quotes.js.map